import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Screenshot } from '../../components/DocsComponents'

const Planning = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What planning is for</Typography>
      <Typography>
        PTS planning turns bookings into workable duties and routes. Planners
        can create duties, assign vehicles and crew shifts, review unassigned
        journeys, and move journeys between routes.
      </Typography>
      <Typography>
        Open <DocLink url="/pts/planning" text="PTS Planning" />.
      </Typography>
      <Alert severity="info" variant="outlined">
        Planning (like Bookings, Dispatch, Patients, Organisations, Reports,
        and Invoices) requires the <strong>Co-Ordinator</strong> PTS
        permission, set from{' '}
        <DocLink
          url="/site-configuration/pts-access"
          text="Configuration > Patient Transport > PTS Access"
        />
        . It's the only PTS permission — crew work needs none, so every
        employee can see the My PTS Duty screen and be rostered onto a duty.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Vehicle Timeline</Typography>
      <Typography>
        The <strong>Vehicle Timeline</strong> button on the planning list opens
        a gantt-style view (day, 3-day, or week zoom) with one row per vehicle,
        grouped by base location. Duty blocks show a mobility-count meter
        (walking/chair/stretcher) and a warning badge if the load doesn't suit
        the vehicle. Vehicle unavailability periods and event deployments are
        shown as hatched blocks on the same row, so PTS and Events work booked
        onto the same vehicle are finally visible to each other.
      </Typography>
      <Typography>
        Drag an unplanned journey from the tray at the bottom straight onto a
        duty block to assign it — this is the same assignment the planning
        list does, just with a visual, drag-and-drop way to do it.
      </Typography>
      <Screenshot url="patient-transport/vehicle-timeline.png">
        The Vehicle Timeline showing rows per vehicle with duty blocks, a
        mobility-count meter, a hatched unavailability block, and the
        unplanned-journeys tray.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Patient Timeline</Typography>
      <Typography>
        The <strong>Patient Timeline</strong> button next to it opens the same
        gantt-style view flipped the other way round — one row per patient who
        has a journey in the visible day/week, rather than one row per
        vehicle. Each block is a single journey, colour-coded amber when it
        still has no duty assigned, blue once it does, green when complete,
        and grey for cancelled/no-show/aborted.
      </Typography>
      <Typography>
        This is a read-only view for spotting patterns across many patients at
        once — a patient with two journeys the same day, a gap in what should
        be a regular recurring booking, or several still-unassigned journeys
        clustered on one afternoon. Click a block to see the journey's detail
        and jump straight to its booking. There's no drag-and-drop here — use
        the Vehicle Timeline or the planning list to actually assign a
        journey.
      </Typography>
      <Screenshot url="patient-transport/patient-timeline.png">
        The Patient Timeline showing rows per patient with journey blocks
        colour-coded by status, including at least one amber unassigned
        block.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Vehicle capacity enforcement</Typography>
      <Typography>
        Every PTS-capable vehicle records how many walking, wheelchair, and
        stretcher patients it can carry, whether it's bariatric-capable, and
        any escort/wheelchair-restraint capacity — set from the vehicle's own
        page in Fleet.
      </Typography>
      <Typography>
        Assigning a journey onto a duty whose vehicle can't take it (e.g. a
        stretcher patient onto a car) is checked at the moment of assignment,
        on the planning list, the control screen, and the Vehicle Timeline
        alike. Whether a mismatch is only a warning or is rejected outright
        depends on the <strong>Vehicle Capacity Enforcement</strong> setting
        under{' '}
        <DocLink
          url="/site-configuration/pts-bookings"
          text="Configuration > Patient Transport > PTS Bookings"
        />
        : <strong>Warn</strong> (default) allows the assignment but flags it;{' '}
        <strong>Block</strong> rejects it outright.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Block mode is not the default. If capacity mismatches keep slipping
        through to dispatch, check this setting rather than assuming
        assignment is meant to stop them.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Duties</Typography>
      <Typography>
        A duty represents a planned piece of work for a vehicle or crew. Duty
        creation includes base location, duty times, assigned vehicle, existing
        shifts, and crew shifts. Duties can then hold routes, and routes hold
        the journeys the crew will complete.
      </Typography>
      <Alert severity="info" variant="outlined">
        Base location options are filtered by the user's selected locations and
        organisation configuration.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Dedicated duties (discharge crews)
      </Typography>
      <Typography>
        On the duty form, <strong>Dedicate this duty to a hospital /
        organisation</strong> reserves the duty — vehicle and crew — for one
        organisation's journeys only (any journey type). A dedicated duty
        never appears as an option for general PTS planning or other
        customers' journeys, and the hospital can book journeys straight onto
        it through its portal, approved automatically — see{' '}
        <DocLink
          url="/patient-transport/hospital-portal"
          text="Hospital Booking Portal"
          docsPage
        />
        . Because the reservation is deliberate, the form asks you to confirm
        ("I understand — reserve this duty for this organisation's journeys")
        before the duty can be saved.
      </Typography>
      <Typography>
        Ticking <strong>No dispatch — the crew add their own jobs</strong>{' '}
        covers the arrangement where the hospital tells the crew about
        discharges and transfers directly, so nothing can be booked in
        advance. Once clocked in, the crew add each job from My PTS Duty and
        fill in the patient and destination as they learn them — see{' '}
        <DocLink
          url="/patient-transport/crew-view"
          text="Crew View"
          docsPage
        />
        . Each job becomes a normal booking for the organisation, flagged for
        a co-ordinator to review afterwards when it will be priced per
        journey.
      </Typography>
      <Typography>
        The duty's <strong>Billing</strong> choice decides how the
        organisation pays: <strong>Per journey</strong> (tariff pricing, the
        default) or <strong>Whole duty</strong> — a fixed price or an hourly
        rate for the crew's time, invoiced by the next billing run after the
        duty ends. Journeys carried on a whole-duty-billed duty are never
        charged individually — they're excluded from per-journey billing
        runs. See{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="Quoting, Invoicing & Billing"
          docsPage
        />
        .
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Journeys and routes</Typography>
      <Typography>
        Journeys can remain unassigned until a planner allocates them. Planning
        views show journey timing such as patient-ready-from time or target
        arrival time, and support assigning journeys into suitable duties or
        routes.
      </Typography>
      <Typography>
        Ward and organisation information follows the journey from the booking,
        so planners can see where hospital work belongs before it reaches
        dispatch.
      </Typography>
      <Typography>
        Any journeys can be moved onto the same route, whatever their journey
        type. The only exception is a patient who must travel alone. Unusual
        runs stay allowed but get a warning on the route, such as{' '}
        <strong>Mixed journey types</strong> or{' '}
        <strong>No shared pickup or destination</strong>.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Route suggestions</Typography>
      <Typography>
        Rather than placing every journey by hand, use{' '}
        <strong>Suggest Routes</strong>. On a duty (future duties only) it
        proposes a full route plan — each suggested route with a score and
        any warnings — which can be applied in one click or dismissed. On the
        unassigned journeys list it works the other way round, suggesting a
        duty and route for each waiting journey, again with a score and{' '}
        <strong>Apply</strong>/<strong>Dismiss</strong>.
      </Typography>
      <Typography>
        The same scoring shows up when assigning a single journey — candidate
        duties are marked <strong>Good fit</strong> or{' '}
        <strong>Check first</strong> based on timing, vehicle/mobility fit,
        and existing route compatibility.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Shifts and crew</Typography>
      <Typography>
        Planning can use existing staff shifts or create crew shifts while a
        duty is being prepared. This connects the operational rota with the PTS
        duty, so crew members can later see their assigned route work in the
        crew-facing PTS view.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Repeating a duty</Typography>
      <Typography>
        When creating a duty from the planning page, turn on{' '}
        <strong>Repeat this duty</strong> to create the same duty on other
        days — pick the days of the week, how often (every week up to every 4
        weeks) and how long the run lasts. The summary shows how many duties
        and crew shifts will be created, and how many seats are assigned or
        left open.
      </Typography>
      <Typography>
        With <strong>Assign the same crew to every duty</strong> on, every
        duty gets the crew set up on the first one. Turn it off to assign each
        duty individually; any seat left empty is advertised as an open
        vacancy. Nothing is created until every duty passes the usual checks
        (vehicle availability, staff roles, leave and training) — any duty
        that fails is marked <strong>Needs attention</strong> with the reason.
        A run is limited to 100 duties.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Shift acknowledgement</Typography>
      <Typography>
        Whenever a named crew member is put on a PTS shift — creating a crew
        shift with the duty, adding one to an existing duty, or reassigning a
        shift to someone else — the shift is marked as needing their
        acknowledgement, and they're notified by email and push ("New PTS
        shift assigned … Please acknowledge").
      </Typography>
      <Typography>
        The crew member acknowledges (or declines, with a reason) from{' '}
        <strong>My Shifts</strong> on the web or the <strong>Shifts</strong>{' '}
        screen in the mobile app. Reassigning a shift resets the
        acknowledgement, so the new person always has to confirm afresh — and
        the previous assignee is told the shift was taken off them. The
        Global Roster can filter shifts by{' '}
        <strong>Not acknowledged</strong> / <strong>Acknowledged</strong> to
        chase outstanding confirmations.
      </Typography>
      <Alert severity="info" variant="outlined">
        This applies to manual assignment. A shift someone applied for
        themselves doesn't need a separate acknowledgement — applying was the
        confirmation.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Advertising open PTS shifts</Typography>
      <Typography>
        Unassigned PTS crew shifts automatically appear on staff members'{' '}
        <strong>Available Shifts</strong> page (web and mobile), tagged with a{' '}
        <strong>Patient Transport</strong> chip and filtered to each person's
        enabled clinical roles — so a duty short of crew can be filled by
        application rather than by ringing round.
      </Typography>
      <Alert severity="info" variant="outlined">
        Unlike Event shifts, identical PTS shifts are never merged into one
        "N shifts available" listing — each PTS slot is advertised
        individually.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Uncovered and unassigned work</Typography>
      <Typography>
        The planning area highlights shifts and journeys that still need
        attention. Use these lists to resolve gaps before dispatch starts work.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Handover to dispatch</Typography>
      <Typography>
        Once duties and routes are ready, dispatchers use{' '}
        <DocLink url="/pts/control" text="PTS Control" />
        to monitor live progress, assign late journeys, open booking records,
        and communicate with crews.
      </Typography>
    </Stack>
  </Stack>
)

export default Planning
